import { useEffect, useRef, useState } from 'react'
import type {
  CommentHubAPI,
  CommentHubComment,
  CommentHubCommentsList,
  CommentHubListPollParams,
  CommentHubPollCamp,
} from 'shared-code/commenthub-api'
import type { DSPostContext } from 'shared-definitions/types'
import { CommentHubAPIContext } from '../../contexts/CommentHubAPIContext'
import { PostContext } from '../../contexts/PostContext'
import {
  HIGHLIGHT_CANDIDATES,
  HighlightComment,
  isLiveComment,
  pickHighlightComment,
  highlightPicks,
} from '../highlight-comment'

export type ConversationPreviewStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable'

export interface ConversationPreview {
  status: ConversationPreviewStatus
  /** `totalComments` of the post (replies included); null until ready. */
  total: number | null
  /** The newest live top-level comment; null until ready or when there is none. */
  newest: CommentHubComment | null
  /** The conversation's ONE highlighted comment (`pickHighlightComment` — the panel draws the
   *  same one first): the featured pick, else the top comment; null until ready or when nothing
   *  qualifies (the surfaces then quote `newest`). */
  highlight: HighlightComment | null
  /** Live top-level comments posted in the last hour; null until ready. `PREVIEW_PAGE_SIZE` = "10+". */
  newInLastHour: number | null
  /** Up to `RECENT_AUTHORS_MAX` distinct authors of the newest live top-level comments, newest
   *  first (the mobile top bar stacks three, the end block two); empty until ready. */
  recentAuthors: CommentHubComment['author'][]
  /** The authors of the newest live top-level comments that carry a poll `pick` (voted AND said
   *  why), distinct, newest first, up to `RECENT_AUTHORS_MAX` — the mobile top bar's poll face
   *  (2026-10-29). Empty until ready, and always empty without a poll-aware list. */
  recentPickAuthors: CommentHubComment['author'][]
  /** When each live top-level comment of the newest page was posted (ms), newest first — "N new
   *  since you last read" (`conversationLastRead.ts`); empty until ready. */
  recentCreatedAt: number[]
  /** The article poll's camps (top-level threads per choice) from the poll-aware list — the
   *  post-vote "N readers who picked this said why" (`campLine`); null until ready, or when the
   *  article has no conversation poll (the list then carries no `poll`). */
  camps: CommentHubPollCamp[] | null
}

/** How many faces the preview keeps (the mobile top bar's stack). */
export const RECENT_AUTHORS_MAX = 3

/** Hints for commentHub's lazy post registration — see `CommentHubAPI.lookupPost`. */
export interface ConversationLookupHints {
  title?: string | null
  url?: string | null
  tags?: readonly string[]
  categories?: readonly string[]
  topics?: readonly string[]
}

/**
 * The `url` hint for `CommentHubAPI.lookupPost`: the canonical link, the path only when WordPress
 * gave none. commentHub stores the first hint it sees as the post's permanent permalink, so a
 * path (resolved against the browser's origin) on staging stored a staging permalink for good.
 */
export function lookupUrlHint(
  post: Pick<DSPostContext, 'canonicalLink' | 'pagePath'> | null | undefined
): string | undefined {
  return post ? post.canonicalLink ?? post.pagePath : undefined
}

/** One page of newest top-level comments — the whole "new in the last hour" window. */
export const PREVIEW_PAGE_SIZE = 10
const HOUR_MS = 60 * 60 * 1000
/** A resolved preview is reused for this long (rail, bar and end block share it), then refetched. */
const CACHE_TTL_MS = 60 * 1000

const IDLE: ConversationPreview = {
  status: 'idle',
  total: null,
  newest: null,
  highlight: null,
  newInLastHour: null,
  recentAuthors: [],
  recentPickAuthors: [],
  recentCreatedAt: [],
  camps: null,
}

type Loaded = Omit<ConversationPreview, 'status'> & { status: 'ready' | 'unavailable' }

/**
 * Whether "Top comment" needs the one extra `sort=top` request: nothing featured, comments to
 * pick from, and more threads than the newest page holds (when it holds them all, it IS the
 * candidate set).
 */
export function needsTopCandidates(list: CommentHubCommentsList): boolean {
  return (
    !pickHighlightComment(highlightPicks(list), []) &&
    list.totalComments > 0 &&
    list.totalThreads > list.comments.length
  )
}

/**
 * The derivation, pure: what the three entry points show from one `listComments` page (newest)
 * and, when `needsTopCandidates`, the `sort=top` page-1 threads.
 */
/** Up to `RECENT_AUTHORS_MAX` distinct authors, in the comments' order. */
function distinctAuthors(comments: readonly CommentHubComment[]): CommentHubComment['author'][] {
  return comments
    .map(c => c.author)
    .filter((a, i, all) => all.findIndex(b => b.id === a.id) === i)
    .slice(0, RECENT_AUTHORS_MAX)
}

export function previewFromList(
  list: CommentHubCommentsList,
  now: number = Date.now(),
  topCandidates?: readonly CommentHubComment[]
): Loaded {
  // Deleted comments are listed with a scrubbed body, held ones to their author only.
  const live = list.comments.filter(isLiveComment)
  const fresh = live.filter(c => {
    const at = Date.parse(c.createdAt)
    return Number.isFinite(at) && now - at <= HOUR_MS
  }).length
  return {
    status: 'ready',
    total: list.totalComments,
    newest: live[0] ?? null,
    highlight: pickHighlightComment(
      highlightPicks(list),
      topCandidates ?? (list.totalThreads <= list.comments.length ? list.comments : [])
    ),
    newInLastHour: Math.min(fresh, PREVIEW_PAGE_SIZE),
    recentAuthors: distinctAuthors(live),
    recentPickAuthors: distinctAuthors(live.filter(c => typeof c.author.pick === 'string')),
    recentCreatedAt: live.map(c => Date.parse(c.createdAt)).filter(at => Number.isFinite(at)),
    camps: list.poll?.camps ?? null,
  }
}

/** "3 NEW" / "10+ NEW" (the cap, `PREVIEW_PAGE_SIZE`): a full page of fresh comments means there
 *  may be more. */
export function formatNewInLastHour(n: number): string {
  return n >= PREVIEW_PAGE_SIZE ? `${PREVIEW_PAGE_SIZE}+ NEW` : `${n} NEW`
}

interface CacheEntry {
  promise: Promise<Loaded>
  result: Loaded | null
  at: number
}

const cache = new Map<string, CacheEntry>()

/** Mounted previews per container — told to refetch by `invalidateConversationPreview`. */
const subscribers = new Map<string, Set<() => void>>()

/** Tests only. */
export function resetConversationPreviewCache(): void {
  cache.clear()
}

/** Calls `onInvalidate` whenever the container's preview is invalidated; returns the unsubscribe. */
export function subscribeConversationPreview(
  containerId: string,
  onInvalidate: () => void
): () => void {
  const set = subscribers.get(containerId) ?? new Set<() => void>()
  set.add(onInvalidate)
  subscribers.set(containerId, set)
  return () => {
    set.delete(onInvalidate)
    if (!set.size) {
      subscribers.delete(containerId)
    }
  }
}

/**
 * The reader has just changed the thread (their comment published): drop the container's entry —
 * resolved or still in flight — and tell every mounted surface (byline, rail card, bar, end
 * block) to refetch. They keep showing their last ready preview until the new one lands.
 */
export function invalidateConversationPreview(containerId: string): void {
  cache.delete(containerId)
  subscribers.get(containerId)?.forEach(notify => notify())
}

/** A still-fresh resolved preview, read synchronously (no flash on a late-mounting surface). */
export function cachedConversationPreview(
  containerId: string,
  now: number = Date.now()
): Loaded | null {
  const entry = cache.get(containerId)
  return entry?.result && now - entry.at <= CACHE_TTL_MS ? entry.result : null
}

/**
 * ONE load per container for every surface on the page: lookupPost → listComments(newest, 1,
 * 10), plus — only when nothing is featured and the newest page does not hold every thread —
 * listComments(top, 1, HIGHLIGHT_CANDIDATES) for the top comment. Deliberately unabortable — the first surface to unmount must not cancel the others' fetch.
 * A failure evicts the entry so a later mount retries.
 */
export function loadConversationPreview(
  api: CommentHubAPI,
  containerId: string,
  hints?: ConversationLookupHints,
  // The article's conversation poll (2026-10-24): each author then carries `pick` (the rail
  // card's tag). Without one the requests are exactly as before.
  poll?: CommentHubListPollParams
): Promise<Loaded> {
  const existing = cache.get(containerId)
  if (existing && (!existing.result || Date.now() - existing.at <= CACHE_TTL_MS)) {
    return existing.promise
  }
  const fetchPreview = async (): Promise<Loaded> => {
    const post = await api.lookupPost(containerId, undefined, hints)
    if (!post) {
      return { ...IDLE, status: 'unavailable' }
    }
    const list = poll
      ? await api.listComments(post.id, 'newest', 1, PREVIEW_PAGE_SIZE, undefined, undefined, poll)
      : await api.listComments(post.id, 'newest', 1, PREVIEW_PAGE_SIZE)
    if (!needsTopCandidates(list)) {
      return previewFromList(list)
    }
    const top = poll
      ? await api.listComments(post.id, 'top', 1, HIGHLIGHT_CANDIDATES, undefined, undefined, poll)
      : await api.listComments(post.id, 'top', 1, HIGHLIGHT_CANDIDATES)
    return previewFromList(list, Date.now(), top.comments)
  }
  // `promise` is assigned below — it settles the entry it belongs to.
  const entry = { result: null, at: Date.now() } as unknown as CacheEntry
  entry.promise = (async (): Promise<Loaded> => {
    try {
      const result = await fetchPreview()
      entry.result = result
      entry.at = Date.now()
      return result
    } catch (error) {
      if (cache.get(containerId) === entry) {
        cache.delete(containerId)
      }
      throw error
    }
  })()
  cache.set(containerId, entry)
  return entry.promise
}

function whenIdle(run: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(run, { timeout: 2000 })
    return () => window.cancelIdleCallback(handle)
  }
  const handle = setTimeout(run, 0)
  return () => clearTimeout(handle)
}

/**
 * The conversation teaser data for the rail card, the mobile bar and the end-of-article block.
 * SSR and the first client render are always `idle` (nothing from the cache leaks into the
 * hydrated markup); the fetch runs after mount, deferred to an idle moment.
 */
export function useConversationPreview(
  containerId: string | null,
  /** The lazy registration's title in place of the page's (a cross poll's thread is registered
   *  under its question — Have your say, 2026-10-29). */
  overrides?: { title?: string }
): ConversationPreview {
  const commentHub = CommentHubAPIContext.useOptionalContainer()
  const api = commentHub?.api ?? null
  const site = commentHub?.site ?? null
  const post = PostContext.useOptionalContainer()
  const [preview, setPreview] = useState<ConversationPreview>(IDLE)
  /** Bumped by `invalidateConversationPreview` — a refetch that keeps the last ready preview. */
  const [version, setVersion] = useState(0)
  /** The container whose ready preview is on screen — a refetch of it keeps it there. */
  const readyFor = useRef<string | null>(null)

  // Hints are read at fetch time only; keyed on the container, not the post object's identity.
  const title = overrides?.title ?? post?.title
  const url = lookupUrlHint(post)
  const tags = post?.tags
  const categories = post?.categories
  const topics = post?.topics
  const pollId = post?.conversationPoll?.id ?? null

  useEffect(() => {
    if (!containerId) {
      return undefined
    }
    return subscribeConversationPreview(containerId, () => setVersion(v => v + 1))
  }, [containerId])

  useEffect(() => {
    if (!containerId) {
      setPreview(IDLE)
      return undefined
    }
    if (!api) {
      setPreview({ ...IDLE, status: 'unavailable' })
      return undefined
    }
    const cached = cachedConversationPreview(containerId)
    if (cached) {
      readyFor.current = cached.status === 'ready' ? containerId : null
      setPreview(cached)
      return undefined
    }
    let alive = true
    // A revalidation (after the reader's post) keeps what is on screen: no neutral flash.
    const revalidating = readyFor.current === containerId
    if (!revalidating) {
      setPreview({ ...IDLE, status: 'loading' })
    }
    const cancel = whenIdle(() => {
      void (async (): Promise<void> => {
        try {
          const result = await loadConversationPreview(
            api,
            containerId,
            {
              title,
              url,
              tags: tags?.map(t => t.label),
              categories: categories?.map(c => c.label),
              topics: topics?.map(t => t.label),
            },
            site && pollId ? { site, pollId } : undefined
          )
          if (alive) {
            readyFor.current = result.status === 'ready' ? containerId : null
            setPreview(result)
          }
        } catch {
          if (alive && !revalidating) {
            setPreview({ ...IDLE, status: 'error' })
          }
        }
      })()
    })
    return () => {
      alive = false
      cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hints ride along, the key is the container
  }, [api, containerId, version])

  return preview
}
